//src/admin/pages/AdminUserActivity.js
import React, { useEffect, useState, useContext } from "react";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { getAdminUsersActivity } from "../../services/adminUserActivity.api";
import { AdminLanguageContext } from "../context/AdminLanguageContext";

function AdminUserActivity() {
  const { lang } = useContext(AdminLanguageContext);

  const [isOpen, setIsOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [filter, setFilter] = useState("all"); // all | today | week | month | range
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  
  const [showLive, setShowLive] = useState(false);

  const isInRange = (dateStr) => {
  if (!dateStr || dateStr === "—") return false;

  const d = new Date(dateStr);
  const now = new Date();

  if (filter === "today") {
    return d.toDateString() === now.toDateString();
  }

  if (filter === "week") {
    const start = new Date();
    start.setDate(now.getDate() - 7);
    return d >= start && d <= now;
  }

  if (filter === "month") {
    return (
      d.getMonth() === now.getMonth() &&
      d.getFullYear() === now.getFullYear()
    );
  }

  if (filter === "range") {
  if (!fromDate || !toDate) return true;

  const start = new Date(fromDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(toDate);
  end.setHours(23, 59, 59, 999);

  return d >= start && d <= end;
}

  return true; // all
};

const LIVE_MINUTES = 3;

const isLive = (u) => {
  const last = u.lastAction !== "—" ? u.lastAction : u.lastVisit;
  if (!last || last === "—") return false;

  const lastDate = new Date(last);
  const now = new Date();

  return (now - lastDate) / 1000 / 60 <= LIVE_MINUTES;
};

const liveUsers = rows.filter(isLive);

const TEXT = {
  EN: {
    title: "User Activity & Orders",
    today: "Today",
    week: "This Week",
    month: "This Month",
    reset: "Reset",
    live: "Live",
    liveCustomers: "Live Customers",
    activeLast: "Active in last",
    minutes: "minutes",
    noLive: "No users online right now",
    name: "Name",
    phone: "Phone",
    lastActivity: "Last Activity",
    email: "Email",
    orders: "Orders",
    spent: "Spent",
    visits: "Visits",
    actions: "Actions",
    lastVisit: "Last Visit",
    lastAction: "Last Action",
    loading: "Loading...",
  },
  AR: {
    title: "نشاط المستخدمين والطلبات",
    today: "اليوم",
    week: "هذا الأسبوع",
    month: "هذا الشهر",
    reset: "إعادة تعيين",
    live: "متصل الآن",
    liveCustomers: "المستخدمون المتصلون",
    activeLast: "نشط خلال آخر",
    minutes: "دقائق",
    noLive: "لا يوجد مستخدمون متصلون حالياً",
    name: "الاسم",
    phone: "الهاتف",
    lastActivity: "آخر نشاط",
    email: "البريد الإلكتروني",
    orders: "الطلبات",
    spent: "المبلغ",
    visits: "الزيارات",
    actions: "الإجراءات",
    lastVisit: "آخر زيارة",
    lastAction: "آخر إجراء",
    loading: "جاري التحميل...",
  },
}[lang];


  useEffect(() => {
    getAdminUsersActivity()
      .then(setRows)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />
      <div className="flex-1 lg:ml-64 w-full max-w-full overflow-x-hidden">
        <AdminHeader setIsOpen={setIsOpen} />

        <main className="px-4 py-6 sm:px-6">
          <h1 className="text-2xl font-bold text-cyan-700 mb-6">
            {TEXT.title}
          </h1>

          <div className="bg-white rounded-xl shadow p-4 mb-4 flex flex-wrap gap-3 items-end">
  {/* QUICK FILTERS */}
  <button
    onClick={() => setFilter("today")}
    className={`px-4 py-2 rounded ${
      filter === "today" ? "bg-cyan-600 text-white" : "bg-gray-100"
    }`}
  >
    {TEXT.today}
  </button>

  <button
    onClick={() => setFilter("week")}
    className={`px-4 py-2 rounded ${
      filter === "week" ? "bg-cyan-600 text-white" : "bg-gray-100"
    }`}
  >
    {TEXT.week}
  </button>

  <button
    onClick={() => setFilter("month")}
    className={`px-4 py-2 rounded ${
      filter === "month" ? "bg-cyan-600 text-white" : "bg-gray-100"
    }`}
  >
    {TEXT.month}
  </button>

  {/* DATE RANGE */}
  <div className="flex gap-2 items-center">
    <input
      type="date"
      value={fromDate}
      onChange={(e) => {
        setFromDate(e.target.value);
        setFilter("range");
      }}
      className="border px-2 py-1 rounded"
    />
    <span className="text-gray-500">→</span>
    <input
      type="date"
      value={toDate}
      onChange={(e) => {
        setToDate(e.target.value);
        setFilter("range");
      }}
      className="border px-2 py-1 rounded"
    />
  </div>

  <button
  onClick={() => setShowLive((v) => !v)}
  className="
    flex items-center gap-2
    px-4 py-2 rounded
    bg-green-600 text-white
    hover:bg-green-700
    shadow
  "
>
  👁️ {TEXT.live}
</button>


  {/* RESET */}
  <button
    onClick={() => {
      setFilter("all");
      setFromDate("");
      setToDate("");
    }}
    className="ml-auto px-4 py-2 rounded bg-red-100 text-red-600 hover:bg-red-200"
  >
     {TEXT.reset}
  </button>
</div>

{showLive && (
  <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-lg font-bold text-green-700">
        {TEXT.liveCustomers} ({liveUsers.length})
      </h2>

      <span className="text-sm text-green-600">
        {TEXT.activeLast} {LIVE_MINUTES} {TEXT.minutes}
      </span>
    </div>

    {liveUsers.length === 0 ? (
      <p className="text-sm text-gray-500">{TEXT.noLive}</p>
    ) : (
      <table className="hidden md:table min-w-full bg-white rounded-lg overflow-hidden">
        <thead className="bg-green-100">
          <tr>
            <th className="p-2 text-left">{TEXT.name}</th>
            <th className="p-2 text-left">{TEXT.phone}</th>
            <th className="p-2 text-left">{TEXT.lastActivity}</th>
          </tr>
        </thead>

        <tbody>
          {liveUsers.map((u) => (
            <tr key={u.id} className="border-t">
              <td className="p-2 font-semibold">{u.name}</td>
              <td className="p-2">{u.phone || "—"}</td>
              <td className="p-2 text-green-700 font-medium">
                {u.lastAction !== "—" ? u.lastAction : u.lastVisit}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
      {/* LIVE USERS - MOBILE */}
<div className="md:hidden space-y-3">
  {liveUsers.length === 0 ? (
    <p className="text-sm text-gray-500">{TEXT.noLive}</p>
  ) : (
    liveUsers.map((u) => (
      <div
        key={u.id}
        className="bg-white rounded-xl shadow p-3 border"
      >
        <div className="font-bold text-green-700">
          {u.name}
        </div>

        {u.phone && (
          <p className="text-sm text-gray-600 mt-1">
            📞 {u.phone}
          </p>
        )}

        <p className="text-sm text-green-700 mt-1 font-semibold">
          {u.lastAction !== "—" ? u.lastAction : u.lastVisit}
        </p>
      </div>
    ))
  )}
</div>
  </div>
)}



          <div className="hidden md:block bg-white rounded-xl shadow overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3">{TEXT.name}</th>
                  <th className="p-3">{TEXT.email}</th>
                  <th className="p-3">{TEXT.orders}</th>
                  <th className="p-3">{TEXT.spent}</th>
                  <th className="p-3">{TEXT.visits}</th>
                  <th className="p-3">{TEXT.actions}</th>
                  <th className="p-3">{TEXT.lastVisit}</th>
                  <th className="p-3">{TEXT.lastAction}</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="p-4 text-center">
                      {TEXT.loading}
                    </td>
                  </tr>
                ) : (
                  rows
                    .filter((u) => isInRange(u.lastAction || u.lastVisit))
                    .map((u) => (                   
                      <tr key={u.id} className="border-t">
                      <td className="p-3 font-semibold">{u.name}</td>
                      <td className="p-3">{u.email}</td>
                      <td className="p-3 text-center">{u.orders}</td>
                      <td className="p-3 text-center">${u.totalSpent}</td>
                      <td className="p-3 text-center">{u.visits}</td>
                      <td className="p-3 text-center">{u.actions}</td>
                      <td className="p-3 text-center">{u.lastVisit}</td>
                      <td className="p-3 text-center">{u.lastAction}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* MOBILE VIEW */}
<div className="md:hidden space-y-4">
  {loading ? (
    <div className="bg-white p-4 rounded-xl shadow text-center">
      {TEXT.loading}
    </div>
  ) : (
    rows
      .filter((u) => isInRange(u.lastAction || u.lastVisit))
      .map((u) => (
        <div
          key={u.id}
          className="bg-white rounded-xl shadow p-4 border"
        >
          {/* NAME */}
          <div className="font-bold text-cyan-700 text-lg">
            {u.name}
          </div>

          {/* EMAIL */}
          {u.email && (
            <p className="text-sm text-gray-600 mt-1">
              📧 {u.email}
            </p>
          )}

          {/* STATS */}
          <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
            <div>
              <span className="font-semibold">{TEXT.orders}:</span>{" "}
              {u.orders}
            </div>

            <div>
              <span className="font-semibold">{TEXT.spent}:</span>{" "}
              ${u.totalSpent}
            </div>

            <div>
              <span className="font-semibold">{TEXT.visits}:</span>{" "}
              {u.visits}
            </div>

            <div>
              <span className="font-semibold">{TEXT.actions}:</span>{" "}
              {u.actions}
            </div>
          </div>

          {/* ACTIVITY */}
          <div className="mt-3 text-xs text-gray-500 space-y-1">
            <div>
              <span className="font-semibold">{TEXT.lastVisit}:</span>{" "}
              {u.lastVisit}
            </div>
            <div>
              <span className="font-semibold">{TEXT.lastAction}:</span>{" "}
              {u.lastAction}
            </div>
          </div>
        </div>
      ))
  )}
</div>

        </main>
      </div>
    </div>
  );
}

export default AdminUserActivity;